'use client';

import React from 'react';
import Link from 'next/link';
import HealthBadge from '@/components/ui/HealthBadge';
import Icon from '@/components/ui/AppIcon';

type HealthStatus = 'healthy' | 'at-risk' | 'critical' | 'recovering' | 'optimized';

interface Twin {
  id: string;
  name: string;
  health: number;
  status: HealthStatus;
  trend: 'up' | 'down' | 'stable';
  lastSimulation: string;
  predictions: string;
}

const twins: Twin[] = [
  {
    id: 'twin-fin-close',
    name: 'Financial Close',
    health: 82,
    status: 'healthy',
    trend: 'up',
    lastSimulation: '1h ago',
    predictions: 'Close in 4.2 days',
  },
  {
    id: 'twin-cash-app',
    name: 'Cash Application',
    health: 61,
    status: 'at-risk',
    trend: 'down',
    lastSimulation: '30min ago',
    predictions: 'DSO +2.1 days risk',
  },
  {
    id: 'twin-order-fulfill',
    name: 'Order Fulfillment',
    health: 44,
    status: 'critical',
    trend: 'down',
    lastSimulation: '15min ago',
    predictions: '23% SLA breach risk',
  },
  {
    id: 'twin-inventory',
    name: 'Inventory Health',
    health: 91,
    status: 'optimized',
    trend: 'stable',
    lastSimulation: '2h ago',
    predictions: 'Stable for 14 days',
  },
];

export default function DigitalTwinSummary() {
  return (
    <div className="bg-card rounded-xl card-glow-accent overflow-hidden flex flex-col">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Digital Twins</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Live health across all active twins</p>
        </div>
        <Link
          href="/digital-twin-workspace"
          className="flex items-center gap-1.5 text-xs text-accent hover:text-accent/80 font-medium transition-colors px-3 py-1.5 bg-accent/10 rounded-lg"
        >
          <Icon name="CpuChipIcon" size={12} />
          Open Workspace
        </Link>
      </div>

      <div className="flex-1 divide-y divide-border">
        {twins.map((twin) => (
          <div key={twin.id} className="px-5 py-3.5 hover:bg-muted/20 transition-colors group">
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium text-foreground truncate">{twin.name}</p>
                  <HealthBadge status={twin.status} size="sm" />
                </div>
                <p className="text-xs text-muted-foreground">{twin.predictions}</p>
              </div>
              <div className="text-right shrink-0">
                <div className="flex items-center gap-1 justify-end mb-1">
                  <span className="text-lg font-bold text-foreground font-tabular">{twin.health}</span>
                  <span className="text-xs text-muted-foreground">/100</span>
                </div>
                <div className={`flex items-center gap-0.5 text-xs justify-end ${
                  twin.trend === 'up' ? 'text-positive' :
                  twin.trend === 'down' ? 'text-negative' : 'text-muted-foreground'
                }`}>
                  <Icon
                    name={twin.trend === 'up' ? 'ArrowUpIcon' : twin.trend === 'down' ? 'ArrowDownIcon' : 'MinusIcon'}
                    size={10}
                  />
                  <span className="text-2xs">{twin.lastSimulation}</span>
                </div>
              </div>
            </div>

            {/* Mini progress bar */}
            <div className="mt-2 h-1 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${twin.health}%`,
                  background:
                    twin.status === 'healthy' || twin.status === 'optimized' ? 'var(--positive)' :
                    twin.status === 'at-risk' ? 'var(--warning)' : 'var(--negative)',
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}